// How long an unverified booking holds its slot before the auto-expiry sweep
// (in App.js) releases it. Shared so the admin "Verify Payments" tab quotes
// the same number back to staff that customers were shown.
export const PENDING_VERIFICATION_MINUTES = 15;

// Refundable security deposit — same for both units, paid at entry. Shared
// so the landing page's pricing display can't drift from what staff quote.
export const SECURITY_DEPOSIT = 10000;

// ─── Farmhouse Configurations ───────────────────────────────────────────────
// Shared by App.js (customer booking flow) and AdminPanel.js (staff tools) so
// prices/weekend rules never drift between the two.
export const CONFIGS = {
  gold: {
    id: 'gold',
    name: 'RIAZ FARMHOUSE',
    unit: 'UNIT — GOLD',
    tagline: "Karachi's Best Farmhouse Experience",
    location: 'Dua Greens, Karachi',
    promoCode: 'RIAZFARMHOUSENOW',
    promoDiscount: 10000,
    promoLabel: 'Get Rs. 10,000 OFF — use code RIAZFARMHOUSENOW at checkout',
    headlines: [
      '🥇 4 Non-Stop ACs — Full Booking Hours, All Areas Cooled',
      '🏊 Swimming Pool — Clean, Maintained & Ready for You',
      '🔥 BBQ & Bonfire Area — Grills, Seating & Good Vibes',
      '💰 Rs. 10,000 OFF — Use Code RIAZFARMHOUSENOW at Checkout',
      '🌿 Lush Green Garden — Cricket, Football & Open Space',
      '📍 Dua Greens, Karachi — Karachi\'s Best Farmhouse Experience',
    ],
    confirmMsg: 'YOU ARE BOOKING RIAZ FARMHOUSE — UNIT "GOLD"',
    maxPersons: 50,
    maxPersonsLabel: '45–50 Persons',
    personSteps: [10, 20, 30, 40, 50],
    isWeekend: (day) => day === 5 || day === 6,
    prices: {
      dayWeekday: 55000, dayWeekend: 75000,
      nightWeekday: 65000, nightWeekend: 75000,
      fullWeekday: 120000, fullWeekend: 150000,
    },
    facilities: [
      { icon: '❄️', title: '4 NON-STOP ACs',           detail: 'Full booking hours — all areas cooled' },
      { icon: '🏊‍♂️', title: 'SWIMMING POOL',            detail: 'Clean, maintained — full access' },
      { icon: '🍖', title: 'BBQ AREA',                  detail: 'Full BBQ setup with grills & seating' },
      { icon: '🔥', title: 'BONFIRE AREA',              detail: 'Dedicated bonfire zone with benches' },
      { icon: '🎮', title: 'INDOOR GAMES',              detail: 'Dabbo, Patti & more' },
      { icon: '🏏', title: 'LUSH GREEN GARDEN',         detail: 'Cricket & football ground — full size' },
      { icon: '🛏️', title: 'ROOMS',                     detail: 'Bedrooms with attached bathrooms' },
      { icon: '🛋️', title: 'FURNISHED LOUNGE',          detail: 'Sofas, seating & deep freezer' },
      { icon: '⚡', title: '24-HOUR GENERATOR',          detail: 'Uninterrupted power backup' },
      { icon: '🍳', title: 'FURNISHED KITCHEN',         detail: 'Gas stove, utensils — fully equipped' },
      { icon: '🍽️', title: 'INDOOR & OUTDOOR DINING',   detail: 'Dining areas for every occasion' },
      { icon: '🕌', title: 'MASJID FACILITY',           detail: 'On-premise mosque for prayers' },
      { icon: '🚿', title: 'WASHROOMS & CHANGING ROOMS',detail: 'With shower area near pool' },
      { icon: '👨‍👩‍👧‍👦', title: 'SAFE & SECURE ENVIRONMENT', detail: 'CCTV surveillance — 24/7 security' },
      { icon: '📍', title: 'DUA GREENS, KARACHI',       detail: 'Premium location — easy access' },
    ],
  },
  silver: {
    id: 'silver',
    name: 'RIAZ FARMHOUSE',
    unit: 'UNIT — SILVER',
    tagline: 'Perfect for Friends & Family | Chill Environment Guaranteed',
    location: 'Gadap Town, Karachi — Near Gulshan-e-Maymar (10 min drive)',
    promoCode: 'RIAZSILVERNOW',
    promoDiscount: 5000,
    promoLabel: 'Get Rs. 5,000 OFF — use code RIAZSILVERNOW at checkout',
    headlines: [
      '🥈 Biggest Pool — 30×50ft, 6ft Deep — Massive Fun!',
      '🎮 Indoor Games — Dabbo, Patti, Foosball & More!',
      '🛏️ 2 Rooms with Attached Bathrooms — Sleep Comfortably',
      '🔥 BBQ & Bonfire Area — Unforgettable Outdoor Nights',
      '🎟️ Rs. 5,000 OFF — Use Code RIAZSILVERNOW at Checkout',
      '📍 Gadap Town, Karachi — Just 10 Min from Gulshan-e-Maymar',
    ],
    confirmMsg: 'YOU ARE BOOKING RIAZ FARMHOUSE — UNIT "SILVER"',
    maxPersons: 30,
    maxPersonsLabel: '25–30 Persons',
    personSteps: [10, 20, 30],
    isWeekend: (day) => day === 0 || day === 5 || day === 6,
    prices: {
      dayWeekday: 30000, dayWeekend: 40000,
      nightWeekday: 35000, nightWeekend: 40000,
      fullWeekday: 65000, fullWeekend: 85000,
    },
    facilities: [
      { icon: '🏊‍♂️', title: 'BIGGEST SWIMMING POOL',   detail: '30ft × 50ft | 6ft Deep' },
      { icon: '🍖', title: 'BBQ AREA',                detail: '' },
      { icon: '🔥', title: 'BONFIRE AREA',            detail: 'With seating' },
      { icon: '🎮', title: 'INDOOR GAMES',            detail: 'Dabbo, Patti, Foosball' },
      { icon: '🏏', title: 'BIG LUSH GREEN GARDEN',   detail: 'Cricket & football' },
      { icon: '🛏️', title: '2 ROOMS',                 detail: '2 attached bathrooms' },
      { icon: '❄️', title: 'DEEP FREEZER',            detail: '' },
      { icon: '⚡', title: '24-HOUR GENERATOR',        detail: 'Uninterrupted power backup' },
      { icon: '🍳', title: 'FURNISHED KITCHEN',       detail: 'Gas cylinder & stove' },
      { icon: '🍽️', title: 'INDOOR & OUTDOOR DINING', detail: 'Multiple dining areas' },
      { icon: '🕌', title: 'MASJID FACILITY',         detail: 'On-premise mosque for prayers' },
      { icon: '👨‍👩‍👧‍👦', title: 'SAFE FOR FAMILIES',       detail: 'Secure environment' },
      { icon: '🪑', title: 'SITTING AREAS',           detail: 'Benches in the ground' },
      { icon: '🚿', title: '2 WASHROOMS + CHANGING ROOMS', detail: 'Shower area right next to pool' },
    ],
  },
};

// Single source of truth for slot pricing — used by the customer booking form
// and the admin slip-scan "standard rate" comparison, so they can't drift apart.
export function calculatePriceForSlot(config, checkInDate, slotType) {
  if (!config || !checkInDate || !slotType) return 0;
  const day = new Date(checkInDate).getDay();
  const isWknd = config.isWeekend(day);
  const p = config.prices;
  if (slotType === '10hour-day')   return isWknd ? p.dayWeekend   : p.dayWeekday;
  if (slotType === '10hour-night') return isWknd ? p.nightWeekend : p.nightWeekday;
  if (slotType === '22hour')       return isWknd ? p.fullWeekend  : p.fullWeekday;
  return 0;
}